import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, map, Observable, startWith, Subscription } from 'rxjs';

import { HeaderItem, RowDataItem } from '@fcom/ui-components/components/table/table.component';
import { Profile } from '@fcom/core-api/login';
import { RootPaths, unsubscribe } from '@fcom/core';
import {
  ButtonSize,
  ButtonTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  LoaderTheme,
  TabTheme,
} from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate';
import { sortBy } from '@fcom/core/utils';
import { finShare } from '@fcom/rx/operators/finShare';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import { CorporateUser, SelectOption } from '@fcom/common/interfaces';

interface DataInterface {
  value: string;
  key: string;
}
interface SearchForm {
  search: AbstractControl<string | undefined>;
  role: AbstractControl<string | undefined>;
}
@Component({
  selector: 'fin-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit, OnDestroy {
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly ButtonSize = ButtonSize;
  protected readonly IconButtonSize = IconButtonSize;
  protected readonly IconButtonTheme = IconButtonTheme;
  protected readonly IconPosition = IconPosition;
  protected readonly LoaderTheme = LoaderTheme;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly TabTheme = TabTheme;

  @Output()
  userSelected = new EventEmitter<string>();

  profile$: Observable<Profile>;
  rowData$: Observable<RowDataItem<DataInterface>[]>;
  roles: SelectOption[] = [{ value: 'A' }, { value: 'T' }, { value: 'B' }];

  headers: HeaderItem[] = [
    { key: 'name' },
    { key: 'email' },
    { key: 'phone' },
    { key: 'role' },
    { key: 'username' },
    { key: 'details' },
  ];
  form: FormGroup<SearchForm>;
  subscriptions = new Subscription();

  constructor(
    private router: Router,
    private corporateService: CorporateService,
    private languageService: LanguageService,
    private elementRef: ElementRef
  ) {}
  ngOnInit(): void {
    this.form = new FormGroup<SearchForm>({
      search: new FormControl(),
      role: new FormControl(''),
    });
    const allUsers$: Observable<CorporateUser[]> = this.corporateService.getUsers().pipe(finShare());
    const filteredUsers$: Observable<CorporateUser[]> = combineLatest([
      allUsers$,
      this.form.valueChanges.pipe(startWith(null)),
    ]).pipe(map(([allUsers, formValues]) => this.filterUsers(allUsers, formValues)));
    this.rowData$ = filteredUsers$.pipe(
      map((users) => {
        const sorted = sortBy(users, (u) => u.lastname.toLowerCase());
        return sorted.map((u) => this.mapUserToTableRow(u));
      })
    );
    this.subscriptions.add(
      this.form.get('search').valueChanges.subscribe((val) => {
        if (val) {
          setTimeout(() => {
            this.highlightSearchTerm(val);
          });
        }
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  addNewUser(): void {
    this.router.navigate([this.languageService.langValue, 'corporate', 'add-user']);
  }

  handleRowClick(data: DataInterface): void {
    this.userSelected.emit(data.value);
  }

  highlightSearchTerm(searchTerm: string): void {
    const search = new RegExp(searchTerm, 'ig');
    // Selects all 'span' elements in the first column of each row
    const spans = this.elementRef.nativeElement.querySelectorAll(
      '.user-list tbody:not(.no-results) tr td:first-child span'
    );
    [...spans].forEach((s: HTMLSpanElement) => {
      const text = s.textContent;
      // Wrap the search term inside of span with background color
      s.innerHTML = text.replace(search, '<span class="nordic-blue-50-bg">$&</span>');
    });
  }

  private filterUsers(
    corporateUsers: CorporateUser[],
    formValues: Partial<{ search: string; role: string }>
  ): CorporateUser[] {
    if (!formValues) {
      return corporateUsers;
    }

    const { search, role } = formValues;

    return corporateUsers.filter((user) => {
      const name = `${user.lastname} ${user.firstname}`.toLowerCase();

      const matchesSearch = this.doesNameMatchSearchTerm(name, search);
      const matchesRole = this.doesRoleMatchSearchRole(user.role, role);

      return matchesSearch && matchesRole;
    });
  }

  private doesNameMatchSearchTerm(name: string, search: string): boolean {
    return search ? name.includes(search.toLowerCase()) : true;
  }

  private doesRoleMatchSearchRole(userRole: string, searchRole: string): boolean {
    return searchRole ? userRole === searchRole : true;
  }

  private mapUserToTableRow(user: CorporateUser) {
    return {
      name: {
        data: {
          value: `${user.lastname} ${user.firstname}`,
          key: 'name',
        },
        options: {
          class: 'bold-type fixed-240',
        },
      },
      email: {
        data: {
          value: user.email,
          key: 'email',
        },
      },
      phone: {
        data: {
          value: user.mobile || user.workPhone,
          key: 'phone',
        },
        options: {
          class: 'fixed-120',
        },
      },
      role: {
        data: {
          value: user.role,
          key: 'role',
        },
        options: {
          class: 'fixed-120',
        },
      },
      username: {
        data: {
          value: user.userId,
          key: 'username',
        },
        options: {
          class: 'fixed-120',
        },
      },
      details: {
        data: {
          value: `${this.languageService.langValue}/${RootPaths.CORPORATE}/admin/user/${user.userId}`,
          key: 'details',
        },
        options: {
          class: 'align-right fixed-48',
        },
      },
      rowAction: {
        data: {
          value: user.userId,
          key: 'rowAction',
        },
      },
    };
  }
}
